import React, { useState, useId } from 'react';
import priceFormatter from '@upgrade/ui-utils/formatters/price';
import { Column, Form, Icon, Margin, Row, TextInput, Tooltip } from '@upgrade/react-components';
import { mdiInformationOutline } from '@mdi/js';
import { priceNumber } from '@upgrade/ui-utils/parsers/extract-numbers';
import Fluid from '../../Fluid';
import { Heading, Text } from '../../common/TypographyPublicSite';
import { StyledCTAButton, StyledControlsContainer, StyledCurrency, StyledSlider } from './styles';
import { currencyMask } from '../../../constants/masks';
import { getYearsLabel } from '../../../utils/strings';
import SavingsChart, { calculateInterestSaved } from './savings-chart';
import { getFormattedMarkdownText } from '../../../utils/get-formatted-markdown-text';

const MIN_INITIAL_DEPOSIT = 1000;
const DEFAULT_FORM_VALUES = {
  depositAmount: 15000
};

const SavingsCalculator = ({
  apy,
  ctaButtonText,
  ctaButtonAriaLabel,
  depositUrl,
  headerSubtitle,
  headerTooltip,
  inputHint,
  inputLabel,
  subtitle,
  title,
  totalInterest,
  totalInterestTooltip,
  totalSavings,
  totalSavingsTooltip,
  yearsBarTitle
}) => {
  const [years, setYears] = useState(5);
  const [initialDeposit, setInitialDeposit] = useState(DEFAULT_FORM_VALUES.depositAmount);
  const totalInterestSaved = calculateInterestSaved(initialDeposit, years, apy);
  const inputLabelId = useId();

  const onFormChange = ({ depositAmount }) => {
    const parsedAmount = priceNumber(depositAmount);
    if (parsedAmount < MIN_INITIAL_DEPOSIT) {
      setInitialDeposit(0);
      return;
    }
    setInitialDeposit(parsedAmount);
  };

  return (
    <Fluid>
      <Row xsHorizontalAlign="center">
        <Column xs={12}>
          <StyledCurrency size="sm">
            <Heading size="lg" as="h2" marginBottom="sm">
              {title}
            </Heading>
          </StyledCurrency>
          <Text size="md" as="p" marginBottom="none" color="contentWeak">
            {getFormattedMarkdownText(subtitle, { apy })}
          </Text>
          <Text size="md" color="contentWeak">
            {headerSubtitle}{' '}
            <Tooltip overlay={headerTooltip} placement="bottom" trigger={['mouseenter']} background="light">
              <Text>
                <Icon icon={mdiInformationOutline} size="small" align="middle" />
              </Text>
            </Tooltip>
          </Text>
        </Column>
      </Row>
      <StyledControlsContainer>
        <Row>
          <Column xs={12} sm={3}>
            <Form initialValues={{ ...DEFAULT_FORM_VALUES }} onChange={onFormChange}>
              <Text id={inputLabelId} size="sm" as="p" marginBottom="md" bold transform="uppercase">
                {inputLabel}
              </Text>
              <TextInput
                ariaLabelledBy={inputLabelId}
                name="depositAmount"
                hints={[inputHint]}
                floating={false}
                mask={currencyMask({ integerLimit: 6 })}
                inputmode="decimal"
              />
            </Form>
          </Column>
          <Column xs={12} sm={4}>
            <Margin left={{ sm: 'sm' }} top={{ xs: 'md', sm: 'none' }}>
              <Text size="sm" as="p" marginBottom="sm" bold transform="uppercase">
                {yearsBarTitle}
              </Text>
              <Text size="sm" as="p" marginBottom="sm" color="contentWeak">
                {getYearsLabel(years)}
              </Text>
              <StyledSlider min={5} max={20} value={years} step={5} onChange={setYears} labelFormatter={() => ''} />
            </Margin>
          </Column>
          <Column xs={12} sm={2} xsHorizontalAlign="start" smHorizontalAlign="center">
            <Margin top={{ xs: 'md', sm: 'none' }} bottom={{ xs: 'sm' }}>
              <Text size="sm" bold as="p" marginBottom="none" transform="uppercase">
                {totalSavings}{' '}
                <Tooltip overlay={totalSavingsTooltip} placement="bottom" trigger={['mouseenter']} background="light">
                  <Text>
                    <Icon icon={mdiInformationOutline} size="small" align="middle" />
                  </Text>
                </Tooltip>
              </Text>
            </Margin>
            <StyledCurrency size="sm">
              {priceFormatter(totalInterestSaved + initialDeposit, { digits: 0 })}
            </StyledCurrency>
          </Column>
          <Column xs={12} sm={3} xsHorizontalAlign="center">
            <Margin top={{ xs: 'sm', sm: 'none' }} bottom={{ xs: 'sm' }}>
              <Text size="sm" color="contentWeak" as="p" marginBottom="xs">
                {totalInterest}{' '}
                <Tooltip overlay={totalInterestTooltip} placement="bottom" trigger={['mouseenter']} background="light">
                  <Text>
                    <Icon icon={mdiInformationOutline} size="small" align="middle" />
                  </Text>
                </Tooltip>
              </Text>
              <Heading size="sm" color="actionablePrimary">
                {priceFormatter(totalInterestSaved, { digits: 0 })}
              </Heading>
              <StyledCTAButton
                {...{
                  ariaLabel: ctaButtonAriaLabel,
                  ctaButtonText,
                  linkUrl: depositUrl,
                  buttonProps: {
                    'data-auto': 'rewards-savings-calculator-cta-button'
                  }
                }}
              />
            </Margin>
          </Column>
        </Row>
      </StyledControlsContainer>
      <Margin bottom="3xl">
        <SavingsChart {...{ initialDeposit, apy, years, totalInterestSaved }} />
      </Margin>
    </Fluid>
  );
};

export default SavingsCalculator;
